import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlineChatBubbleOutline } from 'react-icons/md';
import axios from 'config/axios';
import Header from 'components/Header';
import Loader from 'components/Loader';
import FillAppTabs from 'components/FillAppTabs';
import { type TutorEssaySummary, promptNumber } from 'global/essays';

import './index.css';

function relativeTime(iso: string | null): string {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
}

function FillAppEssays() {
    const navigate = useNavigate();
    const { studentOrganizationId } = useParams<{ studentOrganizationId: string }>();

    const [studentName, setStudentName] = useState('');
    const [essays, setEssays] = useState<TutorEssaySummary[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!studentOrganizationId) return;
        let cancelled = false;
        axios.get(`fillapp/tutor/essays/${studentOrganizationId}/`)
            .then(res => {
                if (cancelled) return;
                setStudentName(res.data.studentName || '');
                setEssays(res.data.essays || []);
            })
            .catch(err => { if (!cancelled) setError(err?.response?.data?.message || 'Failed to load essays.'); });
        return () => { cancelled = true; };
    }, [studentOrganizationId]);

    return (
        <div className='fillEssays'>
            <Helmet><title>{studentName ? `${studentName} - Essays` : 'Essays'}</title></Helmet>
            <Header
                title='Essays'
                prevRoutes={[
                    { name: 'College Apps', path: '/college-apps' },
                    // Only show the student crumb once the name is loaded, so we don't
                    // flash a "Student" placeholder before the fetch resolves.
                    ...(studentName ? [{ name: studentName, path: `/college-apps/review/${studentOrganizationId}` }] : []),
                ]}
            />
            <FillAppTabs studentOrganizationId={studentOrganizationId!} active='essays' />

            {error && <div className='fillEssays__error'>{error}</div>}
            {!essays && !error && <Loader />}

            {essays && essays.length === 0 && (
                <div className='fillEssays__empty'>This student hasn't created any essays yet.</div>
            )}

            {essays && essays.length > 0 && ([
                ['Personal Statement', essays.filter(e => e.essayType !== 'supplement')],
                ['Supplemental Essays', essays.filter(e => e.essayType === 'supplement')],
            ] as Array<[string, typeof essays]>).filter(([, group]) => group.length > 0).map(([groupLabel, group]) => (
                <div key={groupLabel} className='fillEssays__group'>
                    <h3 className='fillEssays__groupHead'>{groupLabel}</h3>
                    <div className='fillEssays__grid'>
                    {group.map(essay => {
                        const drafts = essay.versions || [];
                        const latest = drafts[drafts.length - 1];
                        const isSupp = essay.essayType === 'supplement';
                        const num = isSupp ? null : promptNumber(essay.prompt);
                        // Latest-draft lifecycle from the tutor's side: reviewed = comments sent,
                        // in_review = submitted & awaiting, editing = not yet submitted.
                        const state = !latest ? null
                            : latest.commentCount > 0 ? 'reviewed'
                            : latest.submittedAt ? 'in_review'
                            : 'editing';
                        const unsent = latest?.unsentCount || 0;
                        const open = () => { if (latest) navigate(`/college-apps/essays/version/${latest.id}`); };
                        return (
                            <button
                                key={essay.id}
                                type='button'
                                className='fillEssays__card'
                                onClick={open}
                                disabled={!latest}
                            >
                                <div className='fillEssays__cardTop'>
                                    <span className='fillEssays__cardNum'>
                                        {isSupp ? 'SUPPLEMENT' : num ? `PROMPT ${num}` : 'ESSAY'}
                                    </span>
                                    <span className='fillEssays__chips'>
                                        {/* One tag per card. Unsent drafts outrank "New submission":
                                            if the tutor has started commenting they already know it
                                            was submitted, and unsent is their next action. */}
                                        {unsent > 0 ? (
                                            <span className='fillEssays__chipUnsent'>
                                                <MdOutlineChatBubbleOutline size={12} /> {unsent} Unsent
                                            </span>
                                        ) : state === 'in_review' ? (
                                            <span className='fillEssays__badge'><span className='fillEssays__badgeDot' />New submission</span>
                                        ) : state ? (
                                            <span className={`fillEssays__stateChip fillEssays__stateChip--${state}`}>
                                                {state === 'reviewed' ? 'Reviewed' : 'Editing'}
                                            </span>
                                        ) : null}
                                    </span>
                                </div>
                                {isSupp && essay.collegeName && (
                                    <div className='fillEssays__cardFor'>{essay.collegeName}</div>
                                )}
                                <div className='fillEssays__cardTitle'>{essay.title}</div>
                                {essay.prompt && <p className='fillEssays__cardPrompt'>{essay.prompt}</p>}
                                <div className='fillEssays__cardMeta'>
                                    <span><b>{drafts.length}</b> draft{drafts.length === 1 ? '' : 's'}</span>
                                    {latest && (
                                        <span className={essay.wordLimit && latest.wordCount > essay.wordLimit ? 'fillEssays__wordsOver' : ''}>
                                            <b>{latest.wordCount}</b> words
                                        </span>
                                    )}
                                    <span>edited {relativeTime(essay.modifiedAt)}</span>
                                </div>
                            </button>
                        );
                    })}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default FillAppEssays;
